import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { formatarQuantidade } from '../../utils/formatters';

// Registrar os componentes do Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// Cores para cada turno
const CORES_TURNOS = {
  'Noite': '#8fb4d9',
  'Tarde': '#3b5998',
  'Manhã': '#6b8cae',
  'Madrugada': '#b8cfe0',
  'default': '#d1d5db',
};

// Cores quando selecionado
const CORES_SELECIONADO = '#f59e0b'; // amber-500

const PizzaTurno = ({ dados, loading, onCrossFilter, filtroCruzadoAtivo, semWrapper = false }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 300 });
  const { turnos = [], turnosMaiores = [], percentualTurnosMaiores = 0 } = dados || {};

  // Detectar tamanho do container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({ width: containerRef.current.offsetWidth });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Valores responsivos baseados na largura do container
  const responsiveValues = useMemo(() => {
    const w = containerSize.width;
    // Muito pequeno
    if (w < 200) {
      return { labelSize: 8, tooltipSize: 8, borderWidth: 2, cutout: '55%' };
    }
    // Pequeno (telas intermediárias 1024-1199px)
    if (w < 250) {
      return { labelSize: 9, tooltipSize: 9, borderWidth: 2, cutout: '58%' };
    }
    // Médio (1200-1345px)
    if (w < 320) {
      return { labelSize: 10, tooltipSize: 10, borderWidth: 2, cutout: '58%' };
    }
    // Tablet / Desktop pequeno
    if (w < 400) {
      return { labelSize: 11, tooltipSize: 11, borderWidth: 2, cutout: '60%' };
    }
    // Desktop médio
    if (w < 500) {
      return { labelSize: 12, tooltipSize: 11, borderWidth: 3, cutout: '60%' };
    }
    // Desktop grande
    return { labelSize: 13, tooltipSize: 12, borderWidth: 3, cutout: '60%' };
  }, [containerSize.width]);

  // Handler para clique no gráfico
  const handleChartClick = (event) => {
    if (!chartRef.current || !onCrossFilter) return;

    const elements = getElementAtEvent(chartRef.current, event);
    if (elements.length > 0) {
      const index = elements[0].index;
      const turno = turnos[index]?.turno;
      if (turno) {
        onCrossFilter(turno);
      }
    }
  };

  // Handler para clique na legenda
  const handleLegendClick = (turno) => {
    if (onCrossFilter) {
      onCrossFilter(turno);
    }
  };

  // Verificar se um turno está selecionado
  const isTurnoSelected = (turno) => {
    return filtroCruzadoAtivo === turno;
  };

  // Preparar dados para o gráfico
  const labels = turnos.map(t => t.turno);
  const valores = turnos.map(t => t.countLinhas);
  const percentuais = turnos.map(t => t.percentual);

  // Cores com destaque para item selecionado
  const cores = turnos.map(t =>
    isTurnoSelected(t.turno) ? CORES_SELECIONADO : (CORES_TURNOS[t.turno] || CORES_TURNOS['default'])
  );

  // Border mais grosso para item selecionado
  const borderWidths = turnos.map(t => isTurnoSelected(t.turno) ? responsiveValues.borderWidth + 2 : responsiveValues.borderWidth);
  const borderColors = turnos.map(t => isTurnoSelected(t.turno) ? '#d97706' : '#ffffff');

  // Nomes dos turnos maiores para o centro
  const nomesTurnosMaiores = turnosMaiores.map(t => t.turno).join(' + ');

  const chartData = {
    labels,
    datasets: [
      {
        data: valores,
        backgroundColor: cores,
        borderColor: borderColors,
        borderWidth: borderWidths,
        hoverBorderWidth: 4,
        hoverOffset: 8,
      },
    ],
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: responsiveValues.cutout,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: responsiveValues.tooltipSize,
          weight: 'bold',
        },
        bodyFont: {
          size: responsiveValues.tooltipSize,
        },
        padding: 8,
        cornerRadius: 6,
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const percentual = percentuais[index]?.toFixed(1) || 0;
            const valor = formatarQuantidade(context.raw);
            return ` ${percentual}% (${valor})`;
          },
        },
      },
      datalabels: {
        color: '#ffffff',
        font: {
          weight: 'bold',
          size: responsiveValues.labelSize,
        },
        formatter: (value, context) => {
          const percentual = percentuais[context.dataIndex];
          if (percentual > 10) {
            return `${percentual.toFixed(1)}%`;
          }
          return '';
        },
        textShadowColor: 'rgba(0, 0, 0, 0.3)',
        textShadowBlur: 2,
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 800,
      easing: 'easeOutQuart',
    },
  }), [responsiveValues, percentuais]);

  const conteudo = (
    <div ref={containerRef} className="flex flex-col h-full w-full max-w-full overflow-hidden">
      {!semWrapper && (
        <h3 className="dashboard-card-header">Abastecimentos por turno</h3>
      )}

      {loading ? (
        <div className="fluid-spinner-container">
          <div className="bg-gray-200 animate-pulse rounded-full fluid-chart-container"></div>
        </div>
      ) : turnos.length > 0 ? (
        <div className="flex flex-col items-center justify-center h-full flex-1 min-h-0" style={{ gap: 'var(--space-fluid-sm)' }}>
          {/* Gráfico de Rosca */}
          <div className="relative cursor-pointer flex-shrink-0 fluid-chart-container" style={{ paddingTop: 'var(--space-fluid-xs)' }}>
            <Doughnut
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              onClick={handleChartClick}
            />

            {/* Centro do gráfico */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="fluid-chart-center-text-sm">
                {filtroCruzadoAtivo || nomesTurnosMaiores || 'Turnos'}
              </span>
              <span className={`fluid-chart-center-text-lg ${filtroCruzadoAtivo ? 'text-amber-600' : 'text-gray-800'}`}>
                {filtroCruzadoAtivo
                  ? turnos.find(t => t.turno === filtroCruzadoAtivo)?.percentual?.toFixed(1) || 0
                  : percentualTurnosMaiores.toFixed(1)
                }%
              </span>
              <span className="fluid-chart-center-text-sub">
                {filtroCruzadoAtivo ? 'selecionado' : 'dos Abastec.'}
              </span>
            </div>
          </div>

          {/* Legenda personalizada */}
          <div className="turno-legend-container">
            {turnos.map((item, index) => (
              <div
                key={index}
                onClick={() => handleLegendClick(item.turno)}
                className={`flex items-center rounded-full cursor-pointer transition-all duration-200
                  ${isTurnoSelected(item.turno)
                    ? 'border-2 border-amber-500 bg-amber-50 ring-1 ring-amber-300'
                    : 'border border-gray-300 hover:bg-gray-100'
                  }`}
                style={{ gap: 'var(--space-fluid-xs)', padding: 'calc(var(--space-fluid-xs) * 0.5) var(--space-fluid-xs)' }}
                title={`Clique para filtrar por ${item.turno}`}
              >
                <span
                  className="fluid-legend-dot flex-shrink-0"
                  style={{ backgroundColor: isTurnoSelected(item.turno) ? CORES_SELECIONADO : (CORES_TURNOS[item.turno] || CORES_TURNOS['default']) }}
                ></span>
                <span className={isTurnoSelected(item.turno) ? 'text-amber-800 font-semibold' : 'text-gray-600'} style={{ fontSize: 'var(--text-fluid-xs)' }}>
                  {isTurnoSelected(item.turno) && '✓ '}
                  {item.turno}{' '}
                  <span className="font-semibold">{item.percentual.toFixed(0)}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="fluid-spinner-container text-gray-500" style={{ fontSize: 'var(--text-fluid-sm)' }}>
          Nenhum dado disponível
        </div>
      )}
    </div>
  );

  if (semWrapper) {
    return conteudo;
  }

  return (
    <div className="dashboard-card">
      {conteudo}
    </div>
  );
};

export default PizzaTurno;

